import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const CAUSES = [
  {
    num: "01",
    title: "Promouvoir la santé mentale et le mieux-être",
    tagline: "Parce que la santé mentale concerne chacun de nous.",
    text: [
      "Nous défendons une société où prendre soin de sa santé mentale est aussi naturel que prendre soin de sa santé physique.",
      "Nous voulons briser les tabous, réduire la stigmatisation et soutenir les personnes qui traversent une période de vulnérabilité, de transition ou de changement.",
    ],
    why: "Parce qu'une souffrance psychologique ignorée peut progressivement affecter la joie de vivre, les relations, les projets et l'espoir.",
    stats: [
      { value: "1 personne sur 7", label: "dans le monde vivait avec un trouble mental en 2021." },
    ],
    engagement: "Sensibiliser • Prévenir • Accompagner • Outiller",
    closing: "Chaque vie compte. Chaque personne compte. Chaque histoire compte.",
  },
  {
    num: "02",
    title: "Favoriser l'accessibilité et l'inclusion",
    tagline: "Parce que l'aide ne devrait pas être un privilège.",
    text: [
      "Nous défendons un accès plus humain, équitable et accessible au soutien, à l'accompagnement et aux ressources en santé mentale et en mieux-être.",
    ],
    why: "Parce que trop de personnes ont besoin d'aide sans pouvoir y accéder au moment où elles en ont besoin.",
    stats: [
      { value: "36,6 %", label: "des Canadiens ayant un trouble de l'humeur, d'anxiété ou lié à l'usage de substances ont déclaré des besoins de soins en santé mentale non comblés ou partiellement comblés en 2022." },
      { value: "30,5 %", label: "ont déclaré des besoins de counseling non comblés ou partiellement comblés." },
    ],
    engagement: "Rendre l'aide plus accessible • Réduire les barrières • Inclure • Orienter",
    closing: "Personne ne devrait souffrir seul faute de soutien.",
  },
  {
    num: "03",
    title: "Révéler et développer le potentiel humain",
    tagline: "Parce que chaque personne a quelque chose à apporter.",
    text: [
      "Nous défendons le droit de chaque personne à découvrir sa valeur, développer ses forces, cultiver ses talents et croire en ses possibilités.",
      "Nous croyons que des talents peuvent rester dans l'ombre à cause des blessures, des difficultés, du manque de confiance ou du manque d'occasions.",
    ],
    why: "Parce que développer le potentiel humain, c'est aussi créer des possibilités de participation, d'autonomie et de contribution.",
    stats: [
      { value: "11,3 %", label: "des jeunes Canadiens de 15 à 29 ans n'étaient ni aux études, ni en emploi, ni en formation en 2023-2024." },
    ],
    engagement: "Découvrir • Développer • Valoriser • Donner des occasions de contribuer",
    closing: "Chaque potentiel révélé est une richesse pour la personne et pour la société.",
  },
  {
    num: "04",
    title: "Lutter contre la pauvreté et l'exclusion",
    tagline: "Parce que la dignité ne devrait jamais dépendre des ressources financières.",
    text: [
      "Nous défendons une société où les personnes vulnérables peuvent être vues, entendues, respectées, soutenues et accompagnées vers davantage d'autonomie.",
      "La pauvreté ne touche pas uniquement les conditions matérielles. Elle peut aussi fragiliser la santé mentale, l'estime de soi, les relations, les possibilités et l'espoir.",
    ],
    why: "Parce qu'aider une personne à se relever, c'est aussi lui redonner la possibilité de croire en son avenir et d'agir sur sa vie.",
    stats: [
      { value: "11 %", label: "des Canadiens vivaient sous la mesure officielle de pauvreté en 2024 — soit environ 4,5 millions de personnes." },
    ],
    engagement: "Soutenir • Redonner • Autonomiser • Inclure",
    closing: "La dignité d'une personne ne devrait jamais dépendre de ses moyens.",
  },
  {
    num: "05",
    title: "Transformer les milieux de travail",
    tagline: "Parce que le travail devrait aussi contribuer au mieux-être.",
    text: [
      "Nous défendons des milieux de travail sains, humains, mobilisateurs et épanouissants, où les personnes peuvent développer leur potentiel et contribuer pleinement.",
      "Nous croyons que cette transformation passe notamment par des gestionnaires mieux outillés et un leadership plus humain.",
    ],
    why: "Parce que le stress, la surcharge, les conflits, le désengagement et l'épuisement touchent les personnes comme les organisations.",
    stats: [
      { value: "28,5 %", label: "des employés relevant de la juridiction fédérale disaient être souvent ou toujours stressés par leur charge de travail en 2022." },
      { value: "12 milliards", label: "de journées de travail sont perdues chaque année dans le monde en raison de la dépression et de l'anxiété." },
    ],
    engagement: "Prévenir • Développer • Mobiliser • Faire grandir",
    closing: "La performance durable commence par le bien-être des personnes.",
  },
];

export default function Causes() {
  const [open, setOpen] = useState(null);

  return (
    <section data-testid="causes-section" className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-32 lg:px-24">
      <Reveal>
        <SectionLabel>Les 5 causes que nous défendons</SectionLabel>
        <h2 className="mt-8 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Cinq causes qui nous <em className="text-sage-dark">tiennent à cœur</em>
        </h2>
        <p className="mt-8 max-w-2xl text-lg leading-relaxed text-deep/70">
          Derrière chaque accompagnement se trouve une conviction : chaque personne mérite soutien, dignité et occasions de grandir. Voici les causes qui guident notre action.
        </p>
      </Reveal>

      <div className="mt-14">
        {CAUSES.map((c, i) => {
          const isOpen = open === i;
          return (
            <Reveal key={c.num} delay={i * 0.05}>
              <div data-testid={`cause-${i + 1}`} className="border-t border-deep/10 last:border-b">
                <button
                  type="button"
                  data-testid={`cause-toggle-${i + 1}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="group flex w-full items-baseline gap-6 py-8 text-left md:gap-10"
                >
                  <span className="font-serif text-3xl font-light italic text-gold md:text-4xl">{c.num}</span>
                  <span className="flex-1">
                    <span className={`block font-serif text-2xl font-light leading-snug tracking-tight transition-colors duration-300 group-hover:text-gold sm:text-3xl md:text-4xl ${isOpen ? "text-gold" : "text-deep"}`}>
                      {c.title}
                    </span>
                    <span className="mt-2 block font-serif text-lg italic text-deep/50">{c.tagline}</span>
                  </span>
                  <Plus className={`h-6 w-6 shrink-0 self-center text-deep/40 transition-transform duration-500 group-hover:text-gold ${isOpen ? "rotate-45 text-gold" : ""}`} />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="grid gap-10 pb-10 pl-0 md:grid-cols-2 md:pl-24">
                        <div className="space-y-4">
                          {c.text.map((t, j) => (
                            <p key={j} className="leading-relaxed text-deep/70">{t}</p>
                          ))}
                          <div>
                            <p className="text-xs uppercase tracking-[0.28em] text-gold">Pourquoi cette cause ?</p>
                            <p className="mt-3 leading-relaxed text-deep/60">{c.why}</p>
                          </div>
                        </div>
                        <div className="space-y-8">
                          {c.stats.map((s, j) => (
                            <div key={j} className="border-l-2 border-gold pl-6">
                              <p className="font-serif text-5xl font-light tracking-tight text-gold md:text-6xl">{s.value}</p>
                              <p className="mt-2 max-w-md text-sm leading-relaxed text-deep/60">{s.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-4 border-t border-deep/5 pb-12 pt-8 pl-0 md:ml-24 md:flex-row md:items-center md:justify-between">
                        <p className="text-xs uppercase tracking-[0.25em] text-sage-dark">{c.engagement}</p>
                        <p className="font-serif text-xl italic leading-relaxed text-deep/75">{c.closing}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
